import * as React from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { cn } from '../../lib/utils'
import { RefObject, useMemo, useState, useEffect, useCallback } from 'react'

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    maxHeight?: number | string
    maxWidth?: number | string
    viewportRef?: RefObject<HTMLDivElement>
    viewportClassName?: string
    scrollThumbClassName?: string
  }
>(
  (
    {
      className,
      children,
      viewportRef,
      scrollThumbClassName,
      maxHeight,
      maxWidth,
      viewportClassName,
      ...props
    },
    ref,
  ) => {
    const sizingStyle = useMemo(() => {
      if (typeof maxWidth !== 'undefined') {
        return {
          maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
          height: '100%',
        }
      }

      if (typeof maxHeight !== 'undefined') {
        return {
          maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
          width: '100%',
        }
      }

      return {}
    }, [maxHeight, maxWidth])

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={cn('relative overflow-hidden', className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={viewportRef}
          className={cn('rounded-[inherit] [&>div]:!block', viewportClassName)}
          style={sizingStyle}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar scrollThumbClassName={scrollThumbClassName} />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    )
  },
)
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> & {
    scrollThumbClassName?: string
  }
>(({ className, orientation = 'vertical', scrollThumbClassName, ...props }, ref) => {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      ref={ref}
      orientation={orientation}
      className={cn(
        'flex touch-none select-none transition-colors z-[11]',
        orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-[1px]',
        orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        className={cn('relative flex-1 rounded-full bg-neutral-200', scrollThumbClassName)}
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
})
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

const ScrollAreaGradient = ({
  scrollAreaRef,
  direction = 'vertical',
  fadeToColor = 'grey',
}: {
  scrollAreaRef: RefObject<HTMLDivElement>
  direction?: 'vertical' | 'horizontal'
  fadeToColor?: 'grey' | 'white'
}) => {
  const [isOverflowing, setIsOverflowing] = useState({
    vertical: false,
    horizontal: false,
  })

  const checkOverflow = useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current

      // Check vertical overflow
      const hasVerticalOverflow = scrollArea.scrollHeight - scrollArea.clientHeight > 0
      const isScrolledToBottom =
        scrollArea.scrollTop + scrollArea.clientHeight >= scrollArea.scrollHeight - 36

      // Check horizontal overflow
      const hasHorizontalOverflow = scrollArea.scrollWidth - scrollArea.clientWidth > 0
      const isScrolledToRight =
        scrollArea.scrollLeft + scrollArea.clientWidth >= scrollArea.scrollWidth - 36

      setIsOverflowing({
        vertical: hasVerticalOverflow && !isScrolledToBottom,
        horizontal: hasHorizontalOverflow && !isScrolledToRight,
      })
    }
  }, [scrollAreaRef])

  useEffect(() => {
    const scrollArea = scrollAreaRef.current

    const resizeObserver = new ResizeObserver(checkOverflow)

    // Use MutationObserver to detect DOM changes within the scroll area
    const mutationObserver = new MutationObserver(checkOverflow)

    if (scrollArea) {
      // Observe size changes
      resizeObserver.observe(scrollArea)

      // Observe DOM changes (added/removed nodes, attribute changes)
      mutationObserver.observe(scrollArea, {
        childList: true,
        subtree: true,
      })

      // Listen for scroll events
      scrollArea.addEventListener('scroll', checkOverflow)

      // Initial check
      checkOverflow()

      // Also check after a small delay to catch any async rendering
      const timeoutId = setTimeout(checkOverflow, 100)

      return () => {
        resizeObserver.disconnect()
        mutationObserver.disconnect()
        scrollArea.removeEventListener('scroll', checkOverflow)
        clearTimeout(timeoutId)
      }
    }
  }, [scrollAreaRef, checkOverflow])

  if (!isOverflowing[direction]) {
    return null
  }

  return (
    <>
      {direction === 'vertical' && (
        <div
          className={cn(
            'w-full h-0 z-50 absolute bottom-0 pointer-events-none bg-gradient-to-b from-transparent',
            {
              'to-[#46527C1A]': fadeToColor === 'grey',
              'to-white': fadeToColor === 'white',
            },
          )}
        />
      )}
      {direction === 'horizontal' && (
        <div className='absolute inset-0 pointer-events-none z-30'>
          <div
            className={cn('h-full z-50 w-4 absolute right-0 bg-gradient-to-r from-transparent', {
              'to-[#46527C1A]': fadeToColor === 'grey',
              'to-white': fadeToColor === 'white',
            })}
          />
        </div>
      )}
    </>
  )
}

/**
 * Suitable for scrollable area with scroll in X and Y axis.
 */
const ScrollAreaWithGradients = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    maxHeight?: number | string
    maxWidth?: number | string
    viewportRef?: RefObject<HTMLDivElement>
    viewportClassName?: string
    scrollThumbClassName?: string
    fadeToColor?: 'grey' | 'white'
  }
>(
  (
    {
      className,
      children,
      viewportRef,
      scrollThumbClassName,
      maxHeight,
      maxWidth,
      viewportClassName,
      fadeToColor = 'grey',
      ...props
    },
    ref,
  ) => {
    const [viewportElement, setViewportElement] = useState<HTMLDivElement | null>(null)
    const sizingStyle = useMemo(() => {
      const styles: Record<string, string> = {}

      if (typeof maxWidth !== 'undefined') {
        styles.maxWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth
        styles.width = '100%'
      }

      if (typeof maxHeight !== 'undefined') {
        styles.maxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight
        styles.height = '100%'
      }

      return styles
    }, [maxHeight, maxWidth])

    const handleViewportRef = useCallback(
      (node: HTMLDivElement | null) => {
        setViewportElement(node)
        if (viewportRef) {
          // @ts-expect-error - we know this is valid
          viewportRef.current = node
        }
      },
      [viewportRef],
    )

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={cn('relative overflow-hidden h-full', className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={handleViewportRef}
          className={cn('rounded-[inherit] [&>div]:!block h-full', viewportClassName)}
          style={sizingStyle}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar orientation='vertical' scrollThumbClassName={scrollThumbClassName} />
        <ScrollBar orientation='horizontal' scrollThumbClassName={scrollThumbClassName} />
        <ScrollAreaPrimitive.Corner />
        {viewportElement && (
          <>
            <ScrollAreaGradient
              scrollAreaRef={{ current: viewportElement }}
              direction='vertical'
              fadeToColor={fadeToColor}
            />
            <ScrollAreaGradient
              scrollAreaRef={{ current: viewportElement }}
              direction='horizontal'
              fadeToColor={fadeToColor}
            />
          </>
        )}
      </ScrollAreaPrimitive.Root>
    )
  },
)
ScrollAreaWithGradients.displayName = 'ScrollAreaWithGradients'

export { ScrollArea, ScrollBar, ScrollAreaGradient, ScrollAreaWithGradients }
